import { Grid } from "@material-ui/core";
import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from "@material-ui/lab";
import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import { useField } from "formik";
import * as React from "react";

import { RelodeTheme, SelectInput, useDebounce, useRelodeApp } from "../";
import { NormalizedJobTitle } from "../../domain";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                marginBottom: 0,
            },
            description: {
                color: theme.relode.colors.backgroundText.light,
                fontSize: 14,
            },
        }),
    { name: "NormalizedJobTitleSearch" }
);

export type NormalizedJobTitleOption = {
    externalId: string;
    jobName: string;
    confidencePercentage: number;
    description: string;
};

export interface NormalizedJobTitleSearchProps {
    autoFocus?: boolean;
    className?: string;
    onSelect: (
        normalizedJobTitleOption?: NormalizedJobTitleOption,
        normalizedJobTitle?: NormalizedJobTitle
    ) => void;
    disableClearable?: boolean;
    id: string;
    name?: string;
    error?: boolean;
    fullWidth?: boolean;
    helperText?: string;
    label: string;
    selectedNormalizedJobTitle?: NormalizedJobTitleOption;
    freeSolo?: boolean;
}

export const NormalizedJobTitleSearch = (
    props: NormalizedJobTitleSearchProps
) => {
    const classes = useStyles(props);

    const {
        selectedNormalizedJobTitle,
        onSelect,
        label,
        error,
        helperText,
        disableClearable,
        className,
        freeSolo = false,
    } = props;

    const {
        state: {
            config: { normalizedJobTitleLookupService },
        },
    } = useRelodeApp();

    const [suggestions, setSuggestions] = React.useState<
        NormalizedJobTitleOption[]
    >([]);
    const [normalizedJobTitles, setNormalizedJobTitles] = React.useState<
        NormalizedJobTitle[]
    >([]);
    const [normalizedJobTitleSearchText, setNormalizedJobTitleSearchText] =
        React.useState<string>(selectedNormalizedJobTitle?.jobName ?? "");

    React.useEffect(() => {
        if (!selectedNormalizedJobTitle) return;

        setNormalizedJobTitleSearchText(
            selectedNormalizedJobTitle?.jobName ?? ""
        );
        setSuggestions([selectedNormalizedJobTitle]);
    }, [selectedNormalizedJobTitle]);

    const debounceSearchNormalizedJobTitles = useDebounce(
        async (searchText: string) => {
            if (
                !searchText ||
                searchText.trim() == "" ||
                searchText.trim().length === 0
            ) {
                return [];
            }
            return await normalizedJobTitleLookupService.loadSuggestions(
                searchText
            );
        },
        750,
        []
    );

    const handleInputChange = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason === "reset" || reason === "clear") {
            setNormalizedJobTitleSearchText("");
            setSuggestions([]);
            return;
        }

        setNormalizedJobTitleSearchText(value);
        const normalizedJobTitles = await debounceSearchNormalizedJobTitles(
            value
        );
        setNormalizedJobTitles(normalizedJobTitles);

        setSuggestions(
            normalizedJobTitles.map(
                njt =>
                    ({
                        externalId: njt.externalId,
                        jobName: njt.jobName,
                        confidencePercentage: njt.confidencePercentage,
                        description: njt.jobPositions
                            .map(jp => jp.jobPositionName)
                            .join(" \u2022 "),
                    } as NormalizedJobTitleOption)
            )
        );
    };

    const getNormalizedJobTitleOption = (
        value: string | NormalizedJobTitleOption
    ) =>
        !!value
            ? typeof value === "string"
                ? ({
                      externalId: "",
                      jobName: value,
                      description: "",
                      confidencePercentage: 1.0,
                  } as NormalizedJobTitleOption)
                : value
            : null;

    const handleSelectionChanged = (
        e: never,
        newValue: string | NormalizedJobTitleOption,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string | NormalizedJobTitleOption>
    ) => {
        const selectedValue = getNormalizedJobTitleOption(newValue);

        const selectedNormalizedJobTitle = !!selectedValue
            ? normalizedJobTitles.find(
                  njt => njt.jobName === selectedValue.jobName
              ) ??
              ({
                  externalId: selectedValue.externalId,
                  jobName: selectedValue.jobName,
                  confidencePercentage: selectedValue.confidencePercentage,
                  jobPositions: [],
              } as NormalizedJobTitle)
            : null;

        if (!!selectedValue) {
            setNormalizedJobTitleSearchText(selectedValue.jobName);
            setSuggestions([selectedValue]);
            onSelect(selectedValue, selectedNormalizedJobTitle);
        } else {
            setNormalizedJobTitleSearchText("");
            setSuggestions([]);
            onSelect(null, null);
        }
    };

    const handleOnBlur = () => {
        if (!selectedNormalizedJobTitle) {
            setSuggestions([]);
        }
    };

    const extendedId = `${props.id}NormalizedJobTitleSearch`;

    return (
        <div
            className={classnames(classes.root, className)}
            id={`${extendedId}Container`}
        >
            <SelectInput
                mode="suggest"
                freeSolo={freeSolo}
                clearable={!disableClearable}
                value={selectedNormalizedJobTitle}
                error={error}
                getOptionLabel={o =>
                    getNormalizedJobTitleOption(o).jobName ?? ""
                }
                getOptionSelected={(o, v) =>
                    getNormalizedJobTitleOption(o).externalId ===
                    getNormalizedJobTitleOption(v).externalId
                }
                helperText={helperText}
                id={extendedId}
                label={label}
                options={suggestions}
                onChange={handleSelectionChanged}
                onInputChange={handleInputChange}
                noOptionsText="Your keywords did not match any job title"
                inputValue={normalizedJobTitleSearchText}
                onBlur={handleOnBlur}
                renderOption={(o, s) => {
                    const normalizedJobTitleOption =
                        getNormalizedJobTitleOption(o);

                    return (
                        <Grid container>
                            <Grid item xs={12}>
                                {normalizedJobTitleOption.jobName}
                            </Grid>
                            <Grid item xs={12} className={classes.description}>
                                {normalizedJobTitleOption.description}
                            </Grid>
                        </Grid>
                    );
                }}
            />
        </div>
    );
};

export const NormalizedJobTitleSearchField = (
    props: NormalizedJobTitleSearchProps & { name: string }
) => {
    const [field, meta] = useField(props.name);
    return (
        <NormalizedJobTitleSearch
            {...props}
            {...field}
            error={!!meta.error}
            helperText={meta.error}
        />
    );
};
